// InspectionDetailsModal.js

import React, { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';


const WipSchedulerDetailsModal = ({ isOpen, onClose, inspection, onEdit, edit = true }) => {

  // Instructions - truncates / expands
  const [showFullInstructions, setShowFullInstructions] = useState(false);
  const instructions = inspection?.user_comment || '';
  const needsTruncate = instructions.length > 200;
  const visibleInstructions = needsTruncate && !showFullInstructions
    ? instructions.slice(0, 200) + '…'
    : instructions;

  //tech notes - truncates / expands
  const [showFullTechNotes, setShowFullTechNotes] = useState(false);
  const techNotes = inspection?.tech_notes || '';
  const needsTruncateTech = techNotes.length > 200;
  const visibleTechNotes = needsTruncateTech && !showFullTechNotes
    ? techNotes.slice(0, 200) + '…'
    : techNotes;
    

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, options);
  };

  const formatTimeTo12Hour = (time24) => {
    if (!time24) return '';
    const [hourStr, minuteStr] = time24.split(':');
    let hour = parseInt(hourStr, 10);
    const minute = minuteStr;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12; // Adjust 0 and 12 to 12
    return `${hour}:${minute} ${ampm}`;
  };


  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-20" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95">
          <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4 bg-gray-900/30 backdrop-blur-sm">
            <Dialog.Panel className="mx-auto w-full max-w-2xl overflow-hidden rounded-2xl bg-white backdrop-blur-md shadow-xl ring-1 ring-gray-200">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                <div>
                  <Dialog.Title className="text-xl font-semibold text-gray-900">
                    WIP Details
                  </Dialog.Title>
                  <p className="mt-0.5 text-sm text-gray-500">
                    WIP #{inspection?.schedule_id ?? '—'}
                  </p>
                </div>

                {/* Status pill */}
                {inspection?.status && (
                  <span
                    className={
                      `inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset
                      ${
                        inspection.status.toLowerCase() === 'completed'
                          ? 'bg-green-50 text-green-700 ring-green-600/20'
                          : inspection.status.toLowerCase() === 'tentative'
                          ? 'bg-red-50 text-red-700 ring-red-600/20'
                          : 'bg-blue-50 text-blue-700 ring-blue-600/20'
                      }`
                    }
                  >
                    {inspection.status}
                  </span>
                )}
              </div>

              {/* Body */}
              {inspection && (
                <div className="px-6 py-5">
                  {/* Tentative alert */}
                  {inspection.status === 'tentative' && (
                    <div className="mb-5 rounded-xl border border-red-200 bg-red-50 p-4">
                      <p className="text-sm font-semibold text-red-700">Tentative</p>
                      <p className="mt-1 text-sm text-red-700/90">
                        {inspection.status_description || 'No description provided.'}
                      </p>
                    </div>
                  )}

                  {/* Details grid */}
                  <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
                    <div className="rounded-lg bg-gray-50 p-4">
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                        Logged by
                      </dt>
                      <dd className="mt-1 text-sm font-medium text-gray-900">
                        {inspection.user || '—'}
                      </dd>
                    </div>

                    <div className="rounded-lg bg-gray-50 p-4">
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                        Job Number
                      </dt>
                      <dd className="mt-1 text-sm font-medium text-gray-900">
                        {inspection.job_number || '—'}
                      </dd>
                    </div>

                    <div className="rounded-lg bg-gray-50 p-4 sm:col-span-2">
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                        Job Site Address
                      </dt>
                      <dd className="mt-1 text-sm font-medium text-gray-900">
                        {inspection.job_site || '—'}
                      </dd>
                    </div>

                    <div className="rounded-lg bg-gray-50 p-4">
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                        Customer
                      </dt>
                      <dd className="mt-1 text-sm font-medium text-gray-900">
                        {inspection.customer_name || '—'}
                      </dd>
                    </div>

                    <div className="rounded-lg bg-gray-50 p-4">
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                        Lead Technician | Helpers
                      </dt>
                      <dd className="mt-1 text-sm font-medium text-green-900">
                        {inspection.tech_name || '—'}
                      </dd>
                      <dd className="mt-1 text-sm font-light text-gray-500">
                        {inspection.additional_tech_name || '—'}
                      </dd>
                    </div>

                    <div className="rounded-lg bg-gray-50 p-4">
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                        Scheduled Date
                      </dt>
                      <dd className="mt-1 text-sm font-medium text-gray-900">
                        {inspection.schedule_from || '—'}{' '}
                        <span className="text-gray-400">→</span>{' '}
                        {inspection.schedule_to || '—'}
                      </dd>
                    </div>

                    <div className="rounded-lg bg-gray-50 p-4">
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                        Scheduled Time
                      </dt>
                      <dd className="mt-1 text-sm font-medium text-gray-900">
                        {inspection.start_time
                          ? formatTimeTo12Hour(inspection.start_time)
                          : 'No Time'}{' '}
                        –{' '}
                        {inspection.end_time
                          ? formatTimeTo12Hour(inspection.end_time)
                          : 'No Time'}
                      </dd>
                    </div>

                    <div className="rounded-lg bg-green-100 p-4 sm:col-span-2">
                      <dt className="text-xs font-medium uppercase tracking-wide text-green-900">
                        Tech Notes
                      </dt>
                      <dd className="mt-1 whitespace-pre-wrap text-sm text-gray-900">
                        {visibleTechNotes || '—'}
                      </dd>

                      {needsTruncateTech && (
                        <div className="mt-2">
                          <button
                            type="button"
                            onClick={() => setShowFullTechNotes(v => !v)}
                            className="text-sm font-semibold text-indigo-600 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded px-1"
                            aria-expanded={showFullTechNotes}
                          >
                            {showFullTechNotes ? 'Show less' : 'Show more'}
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="rounded-lg bg-gray-50 p-4 sm:col-span-2">
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                        Instructions
                      </dt>
                      <dd className="mt-1 whitespace-pre-wrap text-sm text-gray-900">
                        {visibleInstructions || '—'}
                      </dd>

                      {needsTruncate && (
                        <div className="mt-2">
                          <button
                            type="button"
                            onClick={() => setShowFullInstructions(v => !v)}
                            className="text-sm bg-gray-200 font-semibold text-indigo-600 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded px-1"
                            aria-expanded={showFullInstructions}
                          >
                            {showFullInstructions ? 'Show less' : 'Show more'}
                          </button>
                        </div>
                      )}
                    </div>
                  </dl>

                    {/* Links (only if any) */}
                    {Array.isArray(inspection?.links) && inspection.links.length > 0 && (
                      <div className="rounded-lg bg-gray-50 p-4 sm:col-span-2 mt-2">
                        <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                          Links
                        </dt>
                        <dd className="mt-2 space-y-2">
                          {inspection.links.map((l) => (
                            <div key={l.id} className="flex items-center gap-2">
                              <span className="truncate text-sm font-medium text-gray-900">
                                {l.label || 'Untitled'}
                              </span>
                              <span className="text-gray-300">→</span>
                              <a
                                href={
                                  l.link && /^https?:\/\//i.test(l.link) ? l.link : `https://${l.link}`
                                }
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="truncate text-sm text-indigo-600 hover:underline"
                                title={l.link}
                              >
                                {l.link}
                              </a>
                            </div>
                          ))}
                        </dd>
                      </div>
                    )}

                </div>
              )}

              {/* Footer actions */}
              <div className="flex items-center justify-end gap-2 border-t border-gray-200 bg-gray-50 px-6 py-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex items-center justify-center rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  Close
                </button>
                {edit && (
                  <button
                    type="button"
                    onClick={onEdit}
                    className="inline-flex items-center justify-center rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                  Edit
                </button>
              )}
              </div>
            </Dialog.Panel>
          </div>
          </div>

        </Transition.Child>
      </Dialog>
    </Transition>
  );
};

export default WipSchedulerDetailsModal;